import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
// Utils
import RealtimeUtil from '../utils/RealtimeUtil';
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
import { saveElementsToSnapshot } from '../utils/offline/OfflineRead';
import { canUseFieldWrite, submitFieldWrite } from '../utils/offline/FieldWrite';
import { addElementHistory, createElement, createElements, removeElements, updateElement, updateElements } from '../utils/offline/ElementWrites';
import i18n from '../locales/i18n';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class GreenSpacesService {
    static addGreenSpace(greenSpace, action, shouldShowToast = true) {
        if (!greenSpace.id) {
            greenSpace.id = uuidv4();
            greenSpace.properties.greenSpaceId = greenSpace.id;
            ['baseLine', 'baseRectangle', 'baseCircle'].forEach(property => {
                if (greenSpace.properties[property]) greenSpace.properties[property].propertiesId = greenSpace.id;
            });
            if (greenSpace.properties.woodedAreas)
                for (let i = 0; i < greenSpace.properties.woodedAreas.length; i++)
                    greenSpace.properties.woodedAreas[i].propertiesId = greenSpace.id;
        }
        // Pas de this : méthode passée détachée par ProjectMap
        if (canUseFieldWrite()) return GreenSpacesService.addGreenSpaceThroughOutbox(greenSpace, action, shouldShowToast);
        const promise = Axios.post(BACKEND_ENDPOINT + 'greenSpaces/' + RealtimeUtil.getConnectionId(), greenSpace, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            greenSpace.properties = response.data.greenSpace?.properties;
            console.log('#fetchId', greenSpace.fetchId, response.data.greenSpace?.fetchId);
            greenSpace.fetchId = response.data.greenSpace?.fetchId;
            return response.data;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        if (shouldShowToast) {
            const toastId = 'greenspace_' + (action === 'copyPasting' ? 'copy_pasting' : action);
            const successId = 'greenspace_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
            const errorId = 'greenspace_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
            showLoadingToast(toastId, successId, errorId, promise);
        }
        return promise;
    }

    // Réponse de l'API ({ greenSpace, addedFileInfos… }) ou, en attendant la synchro, { greenSpace, isLocal: true }
    static addGreenSpaceThroughOutbox(greenSpace, action, shouldShowToast) {
        const promise = createElement('Espace vert', greenSpace).then(response => {
            if (response.isLocal) return { greenSpace, isLocal: true };
            greenSpace.properties = response.data.greenSpace?.properties;
            greenSpace.fetchId = response.data.greenSpace?.fetchId;
            return response.data;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        if (shouldShowToast) {
            const toastId = 'greenspace_' + (action === 'copyPasting' ? 'copy_pasting' : action);
            const successId = 'greenspace_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
            const errorId = 'greenspace_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
            showLoadingToast(toastId, successId, errorId, promise);
        }
        return promise;
    }

    static addGreenSpaces(greenSpaces, action, shouldShowToast = true) {
        greenSpaces.forEach(gs => {
            if (!gs.id) {
                gs.id = uuidv4();
                gs.properties.greenSpaceId = gs.id;
                ['baseLine', 'baseRectangle', 'baseCircle'].forEach(property => {
                    if (gs.properties[property]) gs.properties[property].propertiesId = gs.id;
                });
                if (gs.properties.woodedAreas)
                    for (let i = 0; i < gs.properties.woodedAreas.length; i++)
                        gs.properties.woodedAreas[i].propertiesId = gs.id;
            }
        });
        if (canUseFieldWrite()) return GreenSpacesService.addGreenSpacesThroughOutbox(greenSpaces, action, shouldShowToast);

        const promise = Axios.post(BACKEND_ENDPOINT + 'greenSpaces/bulk/' + RealtimeUtil.getConnectionId(), greenSpaces, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            response.data.greenSpaces.forEach(gs => {
                const greenSpace = greenSpaces.find(x => x.id === gs.id);
                greenSpace.properties = gs.properties;
                console.log('#fetchId', greenSpace.fetchId, gs.fetchId);
                greenSpace.fetchId = gs.fetchId;
            });
            return response.data;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        if (shouldShowToast) {
            const prefix = greenSpaces.length > 1 ? 'greenspaces_' : 'greenspace_';
            const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
            const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
            const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
            showLoadingToast(toastId, successId, errorId, promise, greenSpaces.length);
        }
        return promise;
    }

    // Même forme que la réponse de l'API ({ greenSpaces, addedFileInfos, addedOccurrences, updatedSeries }), réunie sur les paquets
    static addGreenSpacesThroughOutbox(greenSpaces, action, shouldShowToast) {
        const promise = createElements('Espace vert', greenSpaces).then(results => {
            const accepted = results.filter(result => !result.isLocal).map(result => result.data);
            const greenSpacesById = new Map(greenSpaces.map(greenSpace => [greenSpace.id, greenSpace]));
            accepted.flatMap(data => data.greenSpaces || []).forEach(serverGreenSpace => {
                const greenSpace = greenSpacesById.get(serverGreenSpace.id);
                if (!greenSpace) return;
                greenSpace.properties = serverGreenSpace.properties;
                greenSpace.fetchId = serverGreenSpace.fetchId;
            });
            return {
                greenSpaces, isLocal: results.some(result => result.isLocal),
                addedFileInfos: accepted.flatMap(data => data.addedFileInfos || []),
                addedOccurrences: accepted.flatMap(data => data.addedOccurrences || []),
                updatedSeries: accepted.flatMap(data => data.updatedSeries || [])
            };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        if (shouldShowToast) {
            const prefix = greenSpaces.length > 1 ? 'greenspaces_' : 'greenspace_';
            const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
            const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
            const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
            showLoadingToast(toastId, successId, errorId, promise, greenSpaces.length);
        }
        return promise;
    }

    static addGreenSpaceHistory(greenSpace, date) {
        if (canUseFieldWrite()) return addElementHistory('Espace vert', greenSpace, date).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'history_added');
            return response.isLocal ? undefined : response.data;
        }, () => {
            showToast('history_addition_failed');
            return;
        });
        return Axios.post(BACKEND_ENDPOINT + 'greenSpaces/history/?date=' + date.toISOString(), greenSpace, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('history_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('history_addition_failed');
            return;
        });
    }

    // saveToSnapshot faux : la carte écrit elle-même la copie, par priorité (chargement par cache Redis)
    static getGreenSpaces(projectId, data, { saveToSnapshot = true } = {}) {
        return Axios.post((!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url)) && (data?.page || data?.page === 0)
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT) + 'greenSpaces/projects/' + projectId, data, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (saveToSnapshot) saveElementsToSnapshot(projectId, 'Espace vert', response.data?.greenSpaces);
            return response.data;
        }, () => {
            if (isOnline() && !data?.cacheKey && !data?.cursor) showToast('greenspaces_loading_failed');
            return;
        });
    }

    static removeGreenSpaces(greenSpaces, shouldShowToast = true) {
        if (canUseFieldWrite()) return GreenSpacesService.removeGreenSpacesThroughOutbox(greenSpaces, shouldShowToast);
        const promise = Axios.delete(BACKEND_ENDPOINT + 'greenSpaces/' + RealtimeUtil.getConnectionId(), {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: greenSpaces
        }).then(response => {
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        let prefixe = greenSpaces.length > 1 ? 'greenspaces' : 'greenspace';
        if (shouldShowToast) {
            showLoadingToast(prefixe + '_deleting', prefixe + '_deleted', prefixe + '_deletion_failed', promise);
        }
        return promise;
    }

    static removeGreenSpacesThroughOutbox(greenSpaces, shouldShowToast) {
        const promise = removeElements('Espace vert', greenSpaces).catch(error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });
        const prefix = greenSpaces.length > 1 ? 'greenspaces' : 'greenspace';
        if (shouldShowToast) showLoadingToast(prefix + '_deleting', prefix + '_deleted', prefix + '_deletion_failed', promise);
        return promise;
    }

    static updateGreenSpace(id, greenSpace, history, action, shouldShowToast = true, previousFeature = null) {
        if (canUseFieldWrite()) return GreenSpacesService.updateGreenSpaceThroughOutbox(id, greenSpace, history, action, shouldShowToast, previousFeature);
        const promise = Axios.put(BACKEND_ENDPOINT + 'greenSpaces/?connectionId=' + RealtimeUtil.getConnectionId() + '&id=' + id + '&history=' + history, greenSpace, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            greenSpace.properties = { ...greenSpace.properties, ...response.data.greenSpace.properties };
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        if (shouldShowToast) {
            const toastId = 'greenspace_' + action;
            const successId = 'greenspace_' + (action === 'cutting' ? 'cut'
                : action === 'splitting' ? 'split'
                    : action.replace('ing', 'ed'));
            const errorId = 'greenspace_' + (['merging', 'updating'].includes(action) ? action.replace('ing', 'e')
                : ['cutting', 'splitting'].includes(action) ? action.substr(0, action.length - 4)
                    : ['deleting', 'rotating', 'subtracting'].includes(action) ? action.replace('ing', 'ion')
                        : action === 'restoring' ? 'restoration'
                            : action) + '_failed';
            showLoadingToast(toastId, successId, errorId, promise);
        }
        return promise;
    }

    static updateGreenSpaceThroughOutbox(id, greenSpace, history, action, shouldShowToast, previousFeature) {
        const promise = updateElement('Espace vert', id, greenSpace, { history }, previousFeature).then(response => {
            if (!response.isLocal) greenSpace.properties = { ...greenSpace.properties, ...response.data.greenSpace.properties };
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        if (shouldShowToast) {
            const toastId = 'greenspace_' + action;
            const successId = 'greenspace_' + (action === 'cutting' ? 'cut'
                : action === 'splitting' ? 'split'
                    : action.replace('ing', 'ed'));
            const errorId = 'greenspace_' + (['merging', 'updating'].includes(action) ? action.replace('ing', 'e')
                : ['cutting', 'splitting'].includes(action) ? action.substr(0, action.length - 4)
                    : ['deleting', 'rotating', 'subtracting'].includes(action) ? action.replace('ing', 'ion')
                        : action === 'restoring' ? 'restoration'
                            : action) + '_failed';
            showLoadingToast(toastId, successId, errorId, promise);
        }
        return promise;
    }

    static bulkUpdateGreenSpaces(greenSpaces, action, shouldShowToast = true, previousFeatures = null) {
        if (canUseFieldWrite()) return GreenSpacesService.bulkUpdateGreenSpacesThroughOutbox(greenSpaces, action, shouldShowToast, previousFeatures);
        const promise = Axios.put(BACKEND_ENDPOINT + 'greenSpaces/bulk/' + RealtimeUtil.getConnectionId(), greenSpaces, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (Array.isArray(response?.data)) {
                response.data.forEach(feature => {
                    let greenSpace = greenSpaces.find(greenSpace => greenSpace.id === feature.id);
                    if (greenSpace) greenSpace.properties = { ...greenSpace.properties, ...feature.properties };
                });
            }

            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        if (shouldShowToast) {
            const toastId = 'greenspaces_' + action;
            const successId = 'greenspaces_' + (action === 'cutting' ? 'cut'
                : action === 'splitting' ? 'split'
                    : action.replace('ing', 'ed'));
            const errorId = 'greenspaces_' + (['merging', 'updating'].includes(action) ? action.replace('ing', 'e')
                : ['cutting', 'splitting'].includes(action) ? action.substr(0, action.length - 4)
                    : ['deleting', 'rotating', 'subtracting'].includes(action) ? action.replace('ing', 'ion')
                        : action === 'restoring' ? 'restoration'
                            : action) + '_failed';
            showLoadingToast(toastId, successId, errorId, promise);
        }
        return promise;
    }

    // Réponse locale si un paquet n'est pas accepté dans le délai ; data contient les espaces verts, complétés par le serveur sinon
    static bulkUpdateGreenSpacesThroughOutbox(greenSpaces, action, shouldShowToast, previousFeatures) {
        const promise = updateElements('Espace vert', greenSpaces, {}, previousFeatures).then(results => {
            const byId = new Map(greenSpaces.map(greenSpace => [greenSpace.id, greenSpace]));
            results.filter(result => !result.isLocal).flatMap(result => Array.isArray(result.data) ? result.data : []).forEach(serverFeature => {
                const greenSpace = byId.get(serverFeature.id);
                if (greenSpace) greenSpace.properties = { ...greenSpace.properties, ...serverFeature.properties };
            });
            return { data: greenSpaces, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        if (shouldShowToast) {
            const toastId = 'greenspaces_' + action;
            const successId = 'greenspaces_' + (action === 'cutting' ? 'cut'
                : action === 'splitting' ? 'split'
                    : action.replace('ing', 'ed'));
            const errorId = 'greenspaces_' + (['merging', 'updating'].includes(action) ? action.replace('ing', 'e')
                : ['cutting', 'splitting'].includes(action) ? action.substr(0, action.length - 4)
                    : ['deleting', 'rotating', 'subtracting'].includes(action) ? action.replace('ing', 'ion')
                        : action === 'restoring' ? 'restoration'
                            : action) + '_failed';
            showLoadingToast(toastId, successId, errorId, promise);
        }
        return promise;
    }

    static async updateZIndex(greenSpaceId, isFirstPlan, projectId = null) {
        if (canUseFieldWrite()) return submitFieldWrite({
            op: { type: 'element.zIndex', projectId, target: { entity: 'zIndex', id: greenSpaceId, category: 'Espace vert' }, refs: [{ entity: 'element', id: greenSpaceId }], cascadeKey: `element:${greenSpaceId}`, payload: { isFirstPlan } },
            buildLocalData: () => null
        });
        return Axios.put(`${BACKEND_ENDPOINT}greenSpaces/zIndex/${greenSpaceId}/${isFirstPlan}`, null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response;
        });
    }

    static getGreenSpaceHistory(id) {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/history/?id=' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('element_history_loading_failed');
            return;
        });
    }

    static getSpaceFunctions() {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/spaceFunctions', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('space_functions_loading_failed');
            return;
        });
    }

    static getSpaceTypes() {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/spaceTypes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('space_types_loading_failed');
            return;
        });
    }

    static getSpaceTypeCategories() {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/spaceTypeCategories', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('space_types_categories_loading_failed');
            return;
        });
    }

    static getRunoffCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/runoffCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('runoff_coefficients_loading_failed');
            return;
        });
    }

    static getDominantCompositions() {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/dominantCompositions', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('dominant_compositions_loading_failed');
            return;
        });
    }

    static getManagementClasses() {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/managementClasses', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('management_classes_loading_failed');
            return;
        });
    }

    static exportGreenSpacesFromProjectAsExcel(name, id, elementIds = [], projection) {
        return Axios.post(BACKEND_ENDPOINT + 'greenSpaces/exportXLSX/' + id, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id} - ${name} (${i18n.t("Espaces verts").toLowerCase()}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
            return true;
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static exportGreenSpaceAsPDF(greenSpace, image, addPrices, photosId, colorless, toastCustomTexts, historyId = null) {
        return Axios.post(BACKEND_ENDPOINT + 'greenSpaces/exportPDF/' + greenSpace.id, { image, addPrices, photosId, colorless, historyId }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            let filename = greenSpace.properties.projectReference ? `${i18n.t("Fiche espace vert")}_${greenSpace.properties.projectReference}` : i18n.t("Fiche espace vert");
            if (greenSpace.properties.customReference) filename += '_' + greenSpace.properties.customReference;
            filename += '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
        });
    }

    static exportGreenSpacesAsPDF(projectId, elementIds, { colorless = false, addPhotos = true } = {}) {
        return Axios.post(BACKEND_ENDPOINT + 'greenSpaces/exportAllPDF/' + projectId, { elementIds, colorless, addPhotos }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
            return;
        });
    }

    static exportAllBacklogsAsPDF(projectId, grouping) {
        return Axios.get(BACKEND_ENDPOINT + 'greenSpaces/exportAllBacklogsPDF/' + grouping + '/' + projectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
            }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('backlogs_pdfs_export_failed');
        });
    }

    static exportGreenSpacesAsSHP(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'greenSpaces/exportSHP/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Espaces verts").toLowerCase()}).zip`);
                document.getElementById('modal-content').appendChild(link);
                link.click();
                document.getElementById('modal-content').removeChild(link);
                URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_shp_export_failed');
        });
    }

    static exportGreenSpacesAsGPKG(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'greenSpaces/exportGPKG/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const blob = new Blob([response.data], { type: 'application/geopackage+sqlite3' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Espaces verts").toLowerCase()}).gpkg`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_gpkg_export_failed');
        });
    }
}
